/* Clip-art personaliser runtime.
 *
 * Runs a Customily option set natively, from a payload produced by extract.py +
 * assets.py. Nothing product-specific lives here, so one copy of this file
 * serves every cloned product.
 *
 * The form is a port of Customily's own storefront script
 * (customily.shopify.script.unified.js), not an interpretation of it:
 *   visibility  evaluateConditionChain / shouldShowOption: each condition needs
 *               its watched option visible and matching, conditions fold left
 *               with and/or, the first condition's action applies
 *   values      applyValueConditions: values hidden or selected by conditions;
 *               a hidden selection moves to the first offered value
 *   rows        COption: options in sort order, label, "(n|max)" counter for
 *               text, "*" when required, help text under the field
 *   faces       SwatchItem: a Swatch shows its thumbnail (or its colour), a Text
 *               Swatch its text; sizes and selection colour from settings
 *   text        TextInput: emojis stripped unless allowed, `only` filter,
 *               max length, upper/lower as typed, trim on blur when trimSpaces
 *   properties  properties[<label verbatim><zero-width spaces for duplicates>]
 *               = value, or text; hide_visually options post nothing
 *   defaults    only values flagged `selected`
 *
 * The preview (references/customily-model.md R3-R8):
 *   layouts     each visible driver's value -> group id; layers in those + shared
 *   artwork     per (group, layer); plates are unbound layers
 *   text        straight boxes that shrink to fit, and text along an ellipse
 *   stacking    zIndex from the design
 *
 * Orders also carry identity, never urls: properties[_design] names the payload
 * version, the layout per visible driver, and each drawn layer by
 * (surface, layer id, asset key or text).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ca]');
  if (!root || root.dataset.caBooted) return;
  root.dataset.caBooted = '1';

  function readJSON(sel) {
    var el = root.querySelector(sel);
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }

  var PRODUCT = readJSON('[data-ca-product]') || { variants: [], options: [] };
  var src = root.getAttribute('data-ca-src');
  if (!src) return;

  fetch(src, { credentials: 'omit' })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .then(function (P) {
      if ((P.schema || 1) < 3) throw new Error('payload schema ' + (P.schema || 1) + ' is too old; re-run extract.py and assets.py');
      run(P);
    })
    .catch(function (e) {
      console.error('[ca-builder] payload failed to load', e);
      var err = root.querySelector('[data-ca-err]');
      if (err) { err.textContent = 'The personaliser could not load. Please refresh.'; err.hidden = false; }
    });

  // Value tuple fields.
  var V_ID = 0, V_VALUE = 1, V_IMAGE = 2, V_SELECTED = 3, V_THUMB = 4, V_BG = 5, V_TIP = 6, V_HIDDEN = 7, V_COND = 8;

  /* ------------------------------------------------------------- TextInput */

  // Customily's emoji test, applied one code point at a time.
  var EMOJI = null;
  try {
    var one = '\\p{Emoji}(?:\\p{EMod}|[\\u{E0020}-\\u{E007E}]+\\u{E007F}|\\uFE0F?\\u20E3?)';
    EMOJI = new RegExp('\\p{RI}{2}|(?![#*\\d](?!\\uFE0F?\\u20E3))' + one + '(?:\\u200D' + one + ')*', 'u');
  } catch (e) { /* engine without unicode property escapes: leave text as typed */ }

  function sanitize(value, only, allowEmojis) {
    var re = null;
    if (only === 'letters') re = /[^a-zA-Z]/g;
    else if (only === 'letters_s') re = /[^a-zA-Z ]/g;
    else if (only === 'numbers') re = /[^0-9]/g;
    else if (only === 'numbers_s') re = /[^0-9 ]/g;
    else if (only) { try { re = new RegExp(only, 'gi'); } catch (e) { re = null; } }
    if (re) value = value.replace(re, '');
    if (!allowEmojis && EMOJI) value = Array.from(value).filter(function (ch) { return !EMOJI.test(ch); }).join('');
    return value;
  }

  function run(P) {
    var ui = P.ui || {};
    var byId = {};
    P.options.forEach(function (o) { byId[o.id] = o; });
    var bySort = P.options.slice().sort(function (a, b) { return a.sort - b.sort; });
    var byIdOrder = P.options.slice().sort(function (a, b) { return a.id - b.id; });
    var curveLayers = P.curveLayers || {};
    var isText = function (o) { return o.type === 'Text Input'; };
    var isDropdown = function (o) { return o.type === 'Dropdown' || o.type === 'Dropdown Swatch'; };

    var sel = {};
    P.options.forEach(function (o) {
      if (isText(o)) { sel[o.id] = ''; return; }
      var def = o.values.filter(function (v) { return v[V_SELECTED] === 1; })[0];
      if (def) sel[o.id] = def[V_ID];
    });

    /* ------------------------------------------------------------- rules */

    function matches(w, c) {
      if (isText(w)) {
        var t = sel[w.id] || '', n = parseInt(String(c.v), 10);
        if (c.o === 'has_length') return t.length >= n;
        if (c.o === 'has_exactly_length') return t.length === n;
        return t == c.v;
      }
      var v = sel[w.id];
      if (v == null) return false;
      return Array.isArray(c.v) ? (c.v.indexOf(-1) !== -1 || c.v.indexOf(v) !== -1) : v == c.v;
    }

    var cache = {}, busy = {};

    function chain(conds) {
      var prev = false, cur = false;
      for (var i = 0; i < conds.length; i++) {
        var c = conds[i], w = byId[c.w];
        if (!w) { cur = true; continue; }
        cur = stateOf(w).show && matches(w, c);
        if (i > 0) {
          if (!c.op) { cur = true; continue; }
          cur = c.op === 'or' ? (cur || prev) : (cur && prev);
        }
        prev = cur;
      }
      return cur;
    }

    function stateOf(o) {
      if (cache[o.id]) return cache[o.id];
      if (busy[o.id]) return { show: true, ui: !o.hidden };      // a cycle: treat as shown
      busy[o.id] = 1;
      var r;
      if (!o.cond.length) {
        r = { show: true, ui: !o.hidden };
      } else {
        var act = o.cond[0].a, res = chain(o.cond);
        if (!act) res = true;
        // make_hidden keeps the option applied and hides only its field
        r = act === 'make_hidden' ? { show: true, ui: !o.hidden && !res }
                                  : { show: act === 'hide' ? !res : res, ui: !o.hidden };
      }
      delete busy[o.id];
      return (cache[o.id] = r);
    }

    function visible(o) { return stateOf(o).show; }
    function shown() { return P.options.filter(visible); }
    function inForm() { return bySort.filter(function (o) { var s = stateOf(o); return s.show && s.ui; }); }

    // applyValueConditions + replaceHiddenSelectedValues.
    var valueHidden = {}, valueMet = {};
    function offered(o, v) { return !v[V_HIDDEN] && !valueHidden[o.id + ':' + v[V_ID]]; }

    function refreshValues() {
      var changed = false;
      P.options.forEach(function (o) {
        if (isText(o) || !visible(o)) return;
        if (!o.values.some(function (v) { return v[V_COND] && v[V_COND].length; })) return;
        var pick = null;
        o.values.forEach(function (v) {
          var c = v[V_COND];
          if (!c || !c.length) return;
          var key = o.id + ':' + v[V_ID], met = chain(c);
          if (c[0].a === 'selected') {
            if (met && !valueMet[key] && !pick) pick = v;
            valueMet[key] = met;
          } else {
            valueHidden[key] = met;
          }
        });
        if (pick && !valueHidden[o.id + ':' + pick[V_ID]] && sel[o.id] !== pick[V_ID]) { sel[o.id] = pick[V_ID]; changed = true; }
        var open = o.values.filter(function (v) { return !v[V_HIDDEN]; });
        var cur = sel[o.id] != null ? selectedValue(o) : open[0];
        if (cur && valueHidden[o.id + ':' + cur[V_ID]]) {
          var next = open.filter(function (v) { return !valueHidden[o.id + ':' + v[V_ID]]; })[0];
          if (next) { if (sel[o.id] !== next[V_ID]) { sel[o.id] = next[V_ID]; changed = true; } }
          else if (sel[o.id] != null) { delete sel[o.id]; changed = true; }
        }
        if (changed) cache = {};
      });
      return changed;
    }

    function settle() {
      cache = {};
      for (var i = 0; i < 6 && refreshValues(); i++) cache = {};
      cache = {};
    }

    function isDriver(id) { return P.drivers.indexOf(id) !== -1; }
    function liveDrivers() { return P.drivers.filter(function (id) { return byId[id] && visible(byId[id]); }); }

    function activeGroups() {
      if (P.drivers.length) {
        var out = [P.shared];
        liveDrivers().forEach(function (id) {
          var g = (P.layouts[String(id)] || {})[String(sel[id])];
          if (g) out.unshift(g);
        });
        return out;
      }
      var all = {};
      [P.imgLayers, P.txtLayers, curveLayers].forEach(function (t) {
        Object.keys(t).forEach(function (k) { all[k] = 1; });
      });
      return Object.keys(all);
    }

    function frameIn(table, layer, groups) {
      for (var i = 0; i < groups.length; i++) {
        var t = table[groups[i]];
        if (t && t[String(layer)]) return { g: groups[i], f: t[String(layer)] };
      }
      return null;
    }

    function selectedValue(o) {
      for (var i = 0; i < o.values.length; i++) if (o.values[i][V_ID] === sel[o.id]) return o.values[i];
      return null;
    }

    function assetKeyFor(group, layer, imageId) {
      var a = (P.art[group] || {})[String(layer)];
      if (!a || imageId == null) return null;
      return a.kind === 'lib' ? 'L' + a.lib + '-P' + imageId : (a.map[String(imageId)] || null);
    }

    function urlFor(key) {
      var f = key && P.files[key];
      return f ? P.assetBase + f : null;
    }

    // What the order line records for an option, exactly as Customily posts it.
    function postedValue(o) {
      if (isText(o)) return sel[o.id] || '';
      var v = selectedValue(o);
      if (!v) return null;
      return v[V_VALUE] || v[V_TIP] || String(v[V_ID]);
    }

    function properties() {
      cache = {};
      var out = {}, labels = [];
      // Customily sorts a set's options by id on load (for a binary search) and
      // numbers duplicate labels in that order.
      byIdOrder.forEach(function (o) {
        if (!visible(o)) return;
        var dup = labels.filter(function (l) { return l === o.label || !o.label; }).length;
        labels.push(o.label);
        if (o.hidden) return;
        var value = postedValue(o);
        if (value == null) return;
        out['properties[' + o.label + new Array(dup + 1).join('​') + ']'] = value;
      });
      return out;
    }

    /* ------------------------------------------------------------- fonts */

    (function loadFonts() {
      var seen = {}, css = '';
      [P.txtLayers, curveLayers].forEach(function (table) {
        Object.keys(table).forEach(function (g) {
          Object.keys(table[g]).forEach(function (id) {
            var k = table[g][id].font;
            if (k && !seen[k] && P.files[k]) {
              seen[k] = 1;
              css += '@font-face{font-family:"ca-' + k + '";src:url("' + P.assetBase + P.files[k] +
                     '");font-display:swap}';
            }
          });
        });
      });
      if (css) {
        var st = document.createElement('style');
        st.textContent = css;
        document.head.appendChild(st);
      }
    })();

    /* ----------------------------------------------------------- preview */

    var stageEl = root.querySelector('[data-ca-stage]');
    var IMAGES = (PRODUCT.images || (PRODUCT.image ? [PRODUCT.image] : [])).filter(Boolean);
    var canvases = [], slides = [], thumbs = [], counter = null;
    var arcSeq = 0;
    var SVG = 'http://www.w3.org/2000/svg';

    // The source's media gallery, with Customily inside it: slide 1 is the first
    // product photo with the live design laid over it in the same box - empty, so
    // the photo shows, until something is chosen; then painted with the surface's
    // background colour behind the design (renderPreview). The other slides are the
    // remaining photos, with thumbnails and an n/N counter. A second surface (a
    // mug's back) follows below the gallery.
    function buildStage() {
      stageEl.textContent = '';
      var gallery = el('div', 'ca-gallery');
      var main = el('div', 'ca-main');
      main.style.aspectRatio = P.surfaces[0].w + ' / ' + P.surfaces[0].h;
      var count = Math.max(1, IMAGES.length);
      for (var i = 0; i < count; i++) {
        var slide = el('div', 'ca-slide');
        slide.setAttribute('data-slide', i);
        if (IMAGES[i]) {
          var ph = el('img', 'ca-photo');
          ph.src = IMAGES[i];
          ph.alt = '';
          if (i) ph.loading = 'lazy';
          slide.appendChild(ph);
        }
        main.appendChild(slide);
        slides.push(slide);
      }
      if (count > 1) {
        counter = el('span', 'ca-count');
        main.appendChild(counter);
      }
      gallery.appendChild(main);
      if (count > 1) {
        var strip = el('div', 'ca-thumbs');
        IMAGES.forEach(function (url, k) {
          var b = el('button', 'ca-thumb');
          b.type = 'button';
          b.setAttribute('aria-label', 'Image ' + (k + 1));
          var im = el('img');
          im.src = url; im.alt = ''; im.loading = 'lazy';
          b.appendChild(im);
          b.addEventListener('click', function () { showSlide(k); });
          strip.appendChild(b);
          thumbs.push(b);
        });
        gallery.appendChild(strip);
      }
      stageEl.appendChild(gallery);

      canvases = P.surfaces.map(function (s, k) {
        var c = el('div', 'ca-canvas');
        if (k === 0) {
          c.classList.add('ca-overlay');
          slides[0].appendChild(c);
        } else {
          stageEl.appendChild(el('p', 'ca-surface-name', s.name));
          var poster = el('div', 'ca-poster');
          poster.style.aspectRatio = s.w + ' / ' + s.h;
          poster.appendChild(c);
          stageEl.appendChild(poster);
        }
        return { el: c, w: s.w, h: s.h, bg: s.bg };
      });
      showSlide(0);
    }

    function showSlide(k) {
      slides.forEach(function (sl, i) { sl.hidden = i !== k; });
      thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === k ? 'true' : 'false'); });
      if (counter) counter.textContent = (k + 1) + '/' + slides.length;
    }

    function place(el, cv, cx, cy, w, h, z, rot) {
      el.style.left = ((cx - w / 2) / cv.w * 100) + '%';
      el.style.top = ((cy - h / 2) / cv.h * 100) + '%';
      el.style.width = (w / cv.w * 100) + '%';
      el.style.height = (h / cv.h * 100) + '%';
      el.style.zIndex = z;
      if (rot) el.style.transform = 'rotate(' + rot + 'deg)';
    }

    function art(cv, url, f) {            // f = [surface, w, h, cx, cy, z, rot]
      var d = document.createElement('div');
      d.className = 'ca-layer';
      place(d, cv, f[3], f[4], f[1], f[2] || f[1], f[5], f[6]);
      var img = document.createElement('img');
      img.src = url; img.alt = ''; img.decoding = 'async';
      d.appendChild(img);
      cv.el.appendChild(d);
    }

    function styled(value, f) { return f.caps ? value.toUpperCase() : value; }

    function straightText(cv, value, f) {
      var d = document.createElement('div');
      d.className = 'ca-text';
      place(d, cv, f.cx, f.cy, f.w, f.h, f.z, f.rot);
      var span = document.createElement('span');
      span.textContent = styled(value, f);
      d.appendChild(span);
      if (f.font && P.files[f.font]) d.style.fontFamily = '"ca-' + f.font + '", sans-serif';
      else d.style.fontWeight = '800';
      if (f.color) d.style.color = f.color;
      d.style.justifyContent = f.align === 'left' ? 'flex-start' : f.align === 'right' ? 'flex-end' : 'center';
      cv.el.appendChild(d);

      // Start from the box height (capped by the design's max) and shrink until
      // the text fits the box, never below the design's minimum.
      var start = f.max ? Math.min(f.h * 0.8, f.max) : f.h * 0.8;
      function setSize(units) {
        d.style.fontSize = (units / cv.w * 100) + 'cqw';
        if (f.ow) d.style.webkitTextStroke = (f.ow * 2 / cv.w * 100) + 'cqw ' + (f.oc || '#000');
      }
      function fit() {
        setSize(start);
        var box = d.clientWidth, need = span.scrollWidth;
        if (box && need > box) setSize(Math.max(f.min || 0, start * box / need * 0.98));
      }
      fit();
      if (f.font && document.fonts) document.fonts.ready.then(fit);
    }

    // Angles are degrees, 0 at the top, clockwise. The arc goes the shorter way
    // round, which reads left to right both along the bottom and over the top.
    function curvedText(cv, value, f) {
      var svg = document.createElementNS(SVG, 'svg');
      svg.setAttribute('class', 'ca-curve');
      svg.setAttribute('viewBox', '0 0 ' + cv.w + ' ' + cv.h);
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.style.zIndex = f.z;
      var rad = function (d) { return d * Math.PI / 180; };
      var pt = function (d) { return [f.cx + f.rx * Math.sin(rad(d)), f.cy - f.ry * Math.cos(rad(d))]; };
      var down = ((f.a0 - f.a1) % 360 + 360) % 360;
      var sweep = down <= 180 ? 0 : 1;
      var span = sweep ? 360 - down : down;
      var p0 = pt(f.a0), p1 = pt(f.a1);
      var id = 'ca-arc-' + (++arcSeq);
      var path = document.createElementNS(SVG, 'path');
      path.setAttribute('id', id);
      path.setAttribute('fill', 'none');
      path.setAttribute('d', 'M ' + p0[0] + ' ' + p0[1] + ' A ' + f.rx + ' ' + f.ry + ' 0 ' +
                             (span > 180 ? 1 : 0) + ' ' + sweep + ' ' + p1[0] + ' ' + p1[1]);
      var text = document.createElementNS(SVG, 'text');
      if (f.font && P.files[f.font]) text.setAttribute('font-family', 'ca-' + f.font);
      text.setAttribute('fill', f.color || '#000');
      if (f.ow) {
        text.setAttribute('stroke', f.oc || '#000');
        text.setAttribute('stroke-width', f.ow * 2);
        text.setAttribute('paint-order', 'stroke fill');
      }
      var anchor = f.align === 'left' ? 'start' : f.align === 'right' ? 'end' : 'middle';
      text.setAttribute('text-anchor', anchor);
      var tp = document.createElementNS(SVG, 'textPath');
      tp.setAttribute('href', '#' + id);
      tp.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#' + id);
      tp.setAttribute('startOffset', anchor === 'start' ? '0%' : anchor === 'end' ? '100%' : '50%');
      tp.textContent = styled(value, f);
      text.appendChild(tp);
      svg.appendChild(path);
      svg.appendChild(text);
      cv.el.appendChild(svg);

      function fit() {
        text.setAttribute('font-size', f.size);
        try {
          var len = path.getTotalLength(), need = text.getComputedTextLength();
          if (len && need > len) text.setAttribute('font-size', Math.max(f.min || 0, f.size * len / need * 0.98));
        } catch (e) { /* not laid out yet */ }
      }
      fit();
      if (f.font && document.fonts) document.fonts.ready.then(fit);
    }

    // Returns the drawn layers too, so the order records exactly what was shown.
    function renderPreview() {
      cache = {};
      canvases.forEach(function (c) { c.el.textContent = ''; c.el.style.backgroundColor = ''; });
      var groups = activeGroups();
      var drawn = [];

      // Customily's canvas stays hidden - plates included - until some visible
      // option has a value; until then the photo under it is what shows.
      var chosen = shown().some(function (o) { return isText(o) ? !!sel[o.id] : sel[o.id] != null; });
      if (!chosen) return drawn;
      // Shown, it paints the surface's bgColor (engraver setBackgroundColor, #ffffff by
      // default), so the photo never shows around art smaller than the surface.
      canvases.forEach(function (c) { c.el.style.backgroundColor = c.bg || '#ffffff'; });

      P.plates.forEach(function (p) {
        if (groups.indexOf(p.group) === -1) return;
        var url = urlFor(p.asset);
        if (url) art(canvases[p.frame[0]], url, p.frame);
      });

      shown().forEach(function (o) {
        if (o.fn.img != null) {
          var hit = frameIn(P.imgLayers, o.fn.img, groups);
          var v = selectedValue(o);
          if (!hit || !v) return;
          var key = assetKeyFor(hit.g, o.fn.img, v[V_IMAGE]);
          var url = urlFor(key);
          if (!url) return;
          art(canvases[hit.f[0]], url, hit.f);
          drawn.push([hit.f[0], o.fn.img, key]);
        } else if (o.fn.txt != null) {
          var t = sel[o.id] || '';
          if (!t.trim()) return;
          var st = frameIn(P.txtLayers, o.fn.txt, groups);
          if (st) {
            straightText(canvases[st.f.s], t, st.f);
            drawn.push([st.f.s, o.fn.txt, t]);
            return;
          }
          var cu = frameIn(curveLayers, o.fn.txt, groups);
          if (cu) {
            curvedText(canvases[cu.f.s], t, cu.f);
            drawn.push([cu.f.s, o.fn.txt, t]);
          }
        }
      });
      return drawn;
    }

    /* -------------------------------------------------------------- form */

    var fieldsEl = root.querySelector('[data-ca-fields]');
    var rowsEl = {};                       // option id -> row, for required messages
    var showRequired = false;

    root.style.setProperty('--ca-sw', (ui.swatch || 46) + 'px');
    root.style.setProperty('--ca-sw-sel', ui.color || '#000');
    root.style.setProperty('--ca-sw-radius', ui.squared === false ? ((ui.swatch || 46) / 2) + 'px' : '4px');

    function el(tag, cls, text) {
      var e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text != null) e.textContent = text;
      return e;
    }

    function helpText(html) {
      // Customily injects help_text as HTML; keep its line breaks, drop markup.
      var d = document.createElement('div');
      d.innerHTML = String(html).replace(/<br\s*\/?>/gi, '\n');
      return d.textContent;
    }

    function textSwatchStyle(b, on) {
      var t = ui.textSwatches;
      if (!t) return;
      var lab = t.label || {}, bg = t.background || {}, stroke = t.stroke || {}, pad = t.padding || {};
      b.style.color = on ? (lab.selectionColor || '#000') : (lab.color || '#000');
      b.style.backgroundColor = on ? (bg.selectionColor || '#FFF') : (bg.color || '#FFF');
      b.style.fontSize = (lab.fontSize || 8) + 'px';
      b.style.border = (stroke.width || 0) + 'px solid ' + (on ? (stroke.selectionColor || '#000') : (stroke.color || '#000'));
      b.style.padding = (pad.topBottom || 0) + 'px ' + (pad.leftRight || 0) + 'px';
      b.style.borderRadius = (stroke.radius || 0) + 'px';
    }

    function choose(o, id) {
      sel[o.id] = id;
      showRequired = false;
      settle();
      renderAll();
    }

    function row(o) {
      var r = el('div', 'ca-opt');
      r.setAttribute('data-opt-id', o.id);
      var name = el('div', 'ca-opt-name');
      name.appendChild(el('span', 'ca-opt-label', o.label));
      var counter = null;
      if (isText(o) && o.max) {
        counter = el('span', 'ca-counter', ' (' + String(sel[o.id] || '').length + '|' + o.max + ')');
        name.appendChild(counter);
      }
      if (o.req) {
        var req = el('span', 'ca-req', ' *');
        var msg = el('span', 'ca-req-msg', ui.requiredText || 'Required');
        msg.style.color = ui.requiredColor || '#f00';
        msg.hidden = !(showRequired && missing(o));
        req.appendChild(msg);
        name.appendChild(req);
      }
      r.appendChild(name);

      if (isText(o)) {
        var inp = el(o.area ? 'textarea' : 'input', 'ca-input');
        if (!o.area) inp.type = 'text';
        if (o.max) inp.maxLength = o.max;
        inp.placeholder = o.ph || '';
        inp.value = sel[o.id] || '';
        inp.addEventListener('input', function () {
          var v = sanitize(inp.value, o.only, o.emoji);
          if (inp.maxLength > 0) v = v.slice(0, inp.maxLength);
          if (o.replace === 'upper') v = v.toUpperCase();
          else if (o.replace === 'lower') v = v.toLowerCase();
          if (inp.value !== v) {
            var pos = Math.min(inp.selectionStart || v.length, v.length);
            inp.value = v;
            try { inp.setSelectionRange(pos, pos); } catch (e) { /* not focusable */ }
          }
          textChanged(o, v, counter);
        });
        inp.addEventListener('blur', function () {
          if (!o.trim) return;
          var t = inp.value.trim();
          if (t !== inp.value) { inp.value = t; textChanged(o, t, counter); }
        });
        r.appendChild(inp);
      } else if (isDropdown(o)) {
        var s = el('select', 'ca-select');
        if (sel[o.id] == null) {
          var none = el('option', null, o.ph || 'Choose an Option');
          none.value = ''; none.disabled = true; none.selected = true;
          s.appendChild(none);
        }
        o.values.forEach(function (v) {
          if (!offered(o, v)) return;
          var opt = el('option', null, v[V_VALUE] || v[V_TIP] || String(v[V_ID]));
          opt.value = String(v[V_ID]);
          opt.setAttribute('data-value-id', v[V_ID]);
          opt.selected = sel[o.id] === v[V_ID];
          s.appendChild(opt);
        });
        s.addEventListener('change', function () { choose(o, +s.value); });
        r.appendChild(s);
      } else {
        var wrap = el('div', 'ca-swatches' + (ui.noWrap ? ' ca-nowrap' : ''));
        wrap.setAttribute('role', 'radiogroup');
        o.values.forEach(function (v) {
          if (!offered(o, v)) return;
          var on = sel[o.id] === v[V_ID];
          var b = el('button', 'ca-sw');
          b.type = 'button';
          b.setAttribute('data-value-id', v[V_ID]);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.setAttribute('aria-label', v[V_VALUE] || v[V_TIP] || String(v[V_ID]));
          if (v[V_TIP]) b.title = v[V_TIP];
          if (o.type === 'Text Swatch') {
            b.classList.add('ca-sw-text');
            b.textContent = v[V_VALUE] == null ? '' : v[V_VALUE];
            textSwatchStyle(b, on);
          } else if (v[V_THUMB] && urlFor(v[V_THUMB])) {
            var img = el('img');
            img.src = urlFor(v[V_THUMB]);
            img.alt = v[V_VALUE] || '';
            img.loading = 'lazy';
            img.width = img.height = 40;
            b.appendChild(img);
            b.style.backgroundColor = 'transparent';
          } else {
            b.style.backgroundColor = v[V_BG] || '#FFF';
          }
          b.addEventListener('click', function () { choose(o, v[V_ID]); });
          wrap.appendChild(b);
        });
        r.appendChild(wrap);
      }

      if (o.help) r.appendChild(el('small', 'ca-help', helpText(o.help)));
      rowsEl[o.id] = r;
      return r;
    }

    function formKey() {
      return inForm().map(function (o) {
        return o.id + ':' + o.values.filter(function (v) { return offered(o, v); }).length;
      }).join(',');
    }

    var lastKey = '';

    // Typing never rebuilds the field being typed in unless the set of fields
    // changes (a condition on text length); then focus and caret come back.
    function textChanged(o, value, counter) {
      sel[o.id] = value;
      if (counter) counter.textContent = ' (' + value.length + '|' + o.max + ')';
      settle();
      if (formKey() !== lastKey) {
        var a = document.activeElement, pos = a && a.selectionStart;
        renderFields();
        var again = fieldsEl.querySelector('.ca-opt[data-opt-id="' + o.id + '"] .ca-input');
        if (again && a && a.classList && a.classList.contains('ca-input')) {
          again.focus();
          try { again.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
        }
      }
      renderPreview();
    }

    function missing(o) {
      if (!o.req) return false;
      return isText(o) ? String(sel[o.id] || '').length === 0 : sel[o.id] == null;
    }

    function renderFields() {
      settle();
      fieldsEl.textContent = '';
      rowsEl = {};
      inForm().forEach(function (o) { fieldsEl.appendChild(row(o)); });
      lastKey = formKey();
    }

    /* ---------------------------------------------------------- variants */

    var optsEl = root.querySelector('[data-ca-options]');
    var priceEl = root.querySelector('[data-ca-price]');
    var cta = root.querySelector('[data-ca-cta]');
    var errEl = root.querySelector('[data-ca-err]');
    var form = root.querySelector('[data-ca-form]');
    var idInput = root.querySelector('[data-ca-id]');
    var selVariant = {};
    PRODUCT.options.forEach(function (o) { selVariant[o.name] = o.values[0]; });

    function variantFor(s) {
      return PRODUCT.variants.filter(function (v) {
        return PRODUCT.options.every(function (o, i) { return v['option' + (i + 1)] === s[o.name]; });
      })[0] || null;
    }

    function variantOffered(name, value) {
      var probe = Object.assign({}, selVariant);
      probe[name] = value;
      var v = variantFor(probe);
      return !!(v && v.available);
    }

    // Theme formatters differ: Minimog's Shopify.formatMoney(cents) reads an
    // unset Shopify.money_format and throws, which stopped renderAll before the
    // preview. Pass the shop's format explicitly and never let price text fail.
    function money(c) {
      var fmt = PRODUCT.moneyFormat;
      if (fmt && window.Shopify && typeof Shopify.formatMoney === 'function') {
        try { return String(Shopify.formatMoney(c, fmt)).replace(/<[^>]*>/g, ''); } catch (e) { /* fall through */ }
      }
      var amount = (c / 100).toFixed(2);
      return fmt ? fmt.replace(/<[^>]*>/g, '').replace(/\{\{\s*\w+\s*\}\}/, amount) : '$' + amount;
    }

    function renderVariants() {
      if (!optsEl) return;
      optsEl.textContent = '';
      PRODUCT.options.forEach(function (o) {
        // Only Shopify's placeholder option of a single-variant product is hidden;
        // a real option with one value is shown, as source themes show it.
        if (o.name === 'Title' && o.values.length === 1 && o.values[0] === 'Default Title') return;
        var f = el('div', 'ca-field');
        f.appendChild(el('span', 'ca-label', o.name));
        var wrap = el('div', 'ca-chips');
        o.values.forEach(function (val) {
          // Source themes show "5.9x5.9 IN - Best Selling" as the size plus a badge.
          var cut = val.lastIndexOf(' - ');
          var b = el('button', 'ca-chip', cut > 0 ? val.slice(0, cut) : val);
          if (cut > 0) b.appendChild(el('span', 'ca-badge', val.slice(cut + 3)));
          b.setAttribute('data-value', val);
          b.type = 'button';
          b.setAttribute('aria-pressed', selVariant[o.name] === val ? 'true' : 'false');
          b.addEventListener('click', function () {
            selVariant[o.name] = val;
            PRODUCT.options.forEach(function (other) {
              if (other.name !== o.name && !variantOffered(other.name, selVariant[other.name])) {
                var ok = other.values.filter(function (x) { return variantOffered(other.name, x); })[0];
                if (ok) selVariant[other.name] = ok;
              }
            });
            renderVariants();
            renderPrice();
          });
          b.disabled = !variantOffered(o.name, val);
          wrap.appendChild(b);
        });
        f.appendChild(wrap);
        optsEl.appendChild(f);
      });
    }

    function renderPrice() {
      var v = variantFor(selVariant);
      if (!v) return;
      priceEl.textContent = money(v.price);
      if (idInput) idInput.value = v.id;
      cta.disabled = !v.available;
      cta.textContent = v.available ? (cta.getAttribute('data-label') || 'Add to cart') : 'Sold out';
    }

    /* ------------------------------------------------------------- order */

    form.addEventListener('submit', function (ev) {
      settle();
      var gaps = inForm().filter(missing);
      if (gaps.length) {
        ev.preventDefault();
        showRequired = true;
        renderFields();
        var first = rowsEl[gaps[0].id];
        if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      errEl.hidden = true;
      form.querySelectorAll('[data-ca-prop]').forEach(function (n) { n.remove(); });
      var add = function (name, value) {
        var input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        input.setAttribute('data-ca-prop', '');
        form.appendChild(input);
      };
      var props = properties();
      Object.keys(props).forEach(function (k) { add(k, props[k]); });
      var picked = {};
      shown().forEach(function (o) { if (sel[o.id] !== undefined && sel[o.id] !== '') picked[o.id] = sel[o.id]; });
      var layout = null;
      var live = liveDrivers();
      if (live.length) { layout = {}; live.forEach(function (id) { layout[id] = sel[id]; }); }
      add('properties[_design]', JSON.stringify({ v: 3, cat: P.version, layout: layout, layers: renderPreview(), sel: picked }));
    });

    function renderAll() {
      renderFields();
      renderVariants();
      renderPrice();
      renderPreview();
    }

    /* -------------------------------------------- phones: pinned preview */

    // Opt-in (section setting mobile_pin_preview), a deliberate difference from
    // Customily: on the one-column layout the preview scrolls away while the shopper
    // personalises, so pin it to the top of the screen until Add to cart has scrolled
    // past. position: sticky cannot do it - themes set overflow-x on html and body.
    function pinPreview() {
      var main = root.querySelector('.ca-main');
      if (!root.hasAttribute('data-ca-pin') || !main || !cta || !window.matchMedia) return;
      var phone = window.matchMedia('(max-width: 959px)');
      var holder = el('div', 'ca-pin-holder');
      holder.hidden = true;
      main.parentNode.insertBefore(holder, main);
      var pinned = false;

      // straight from the scroll event, which browsers already fire at most once a frame
      function update() {
        var anchor = (pinned ? holder : main).getBoundingClientRect();
        var size = Math.min(anchor.width, window.innerHeight * 0.42);
        var want = phone.matches && anchor.top < 0 && cta.getBoundingClientRect().bottom > size;
        if (want !== pinned) {
          pinned = want;
          if (pinned) holder.style.height = anchor.height + 'px';
          holder.hidden = !pinned;
          main.classList.toggle('ca-pinned', pinned);
          document.documentElement.classList.toggle('ca-pin-active', pinned);
        }
        main.style.width = pinned ? size + 'px' : '';
        document.documentElement.style.setProperty('--ca-pin-h', pinned ? size + 'px' : '0px');
      }
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      // a choice made while pinned shows the design, not whichever photo was open
      root.addEventListener('input', function () { if (pinned) showSlide(0); });
      root.addEventListener('click', function (e) {
        if (pinned && e.target.closest && e.target.closest('[data-ca-fields]')) showSlide(0);
      });
      update();
    }

    // For scripts/parity.js and for debugging from the console.
    window.caBuilder = { properties: function () { settle(); return properties(); }, selection: function () { return Object.assign({}, sel); } };

    buildStage();
    renderAll();
    pinPreview();
  }
})();
